import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { QueryState } from '@/components/QueryState'
import { ExportButton } from '@/components/ExportButton'
import { Card, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { useMayOpen } from '@/features/auth/access'
import { useAuth } from '@/features/auth/store'
import {
  STATUS_LABELS,
  currentPeriod,
  exportAccruals,
  formatMoney,
  getStatement,
  listAccruals,
  periodLabel,
  type Accrual,
  type AccrualStatus,
} from '@/features/commissions/api'
import { listLocations } from '@/features/orders/api'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const TONE: Record<AccrualStatus, string> = {
  pending: 'bg-warning/10 text-warning',
  approved: 'bg-success/10 text-success',
  cancelled: 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
}

function StatusBadge({ status }: { status: AccrualStatus }) {
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${TONE[status]}`}>
      {STATUS_LABELS[status]}
    </span>
  )
}

function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</p>
      <p className="text-lg font-semibold tabular-nums">{formatMoney(value)}</p>
      {hint && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{hint}</p>}
    </div>
  )
}

/**
 * How one line was worked out, in the words of the rule that did it.
 *
 * The breakdown is frozen on the accrual rather than recomputed here, so this is what the
 * rep was actually paid on even after somebody edits the rate card next quarter. Shown only
 * when there is more than one rule in play — for a single flat rate the row above already
 * says everything.
 */
function Breakdown({ accrual }: { accrual: Accrual }) {
  if (accrual.breakdown.length < 2) return null
  return (
    <ul className="mt-1 space-y-0.5 text-xs text-slate-500 dark:text-slate-400">
      {accrual.breakdown.map((entry, index) => (
        <li key={`${entry.rule_id ?? 'fallback'}-${index}`}>
          {entry.rule_name}: {formatMoney(entry.basis_amount)} at {Number(entry.rate_pct)}% ={' '}
          {formatMoney(entry.amount)}
        </li>
      ))}
    </ul>
  )
}

/**
 * The row's rate. With one rule in play that rule's own rate: accruals booked before the
 * server stopped doing so carry one worked back from a rounded amount (2.499% for 2.5%).
 */
function displayRate(accrual: Accrual): string {
  const rate = accrual.breakdown.length === 1 ? accrual.breakdown[0].rate_pct : accrual.rate_pct
  return rate === null ? '—' : `${Number(rate)}%`
}

/**
 * One rep, one month: every invoice they earned on and what is payable.
 *
 * Pending and approved are shown apart rather than summed. Under a collection policy the
 * gap between them is "sold, but the customer has not paid yet", which is the number a rep
 * most wants to see and the one a single total would hide.
 */
export function CommissionStatement() {
  const me = useAuth((s) => s.user)
  const [period, setPeriod] = useState(currentPeriod())
  const [userId, setUserId] = useState('')
  const [locationId, setLocationId] = useState('')
  const mayOpenInvoice = useMayOpen('/invoices/:invoiceId')

  const { data: locationsPage } = useQuery({
    queryKey: ['locations'],
    queryFn: () => listLocations(),
    retry: false,
  })
  const locations = locationsPage?.data ?? []

  // The picker's options. Scoped server-side, so a rep sees only themselves and a manager
  // sees their team -- the screen does not have to know which of those it is showing.
  const { data: accruals } = useQuery({
    queryKey: ['commission-accruals', period, locationId],
    queryFn: () => listAccruals({ period, location_id: locationId || undefined }),
    retry: false,
  })

  const reps = useMemo(() => {
    const byId = new Map<string, string>()
    if (me) byId.set(me.id, me.full_name)
    for (const row of accruals?.data ?? []) {
      if (!byId.has(row.user_id)) byId.set(row.user_id, row.user_name ?? 'Unnamed rep')
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [accruals, me])

  const { data, isLoading, error } = useQuery({
    queryKey: ['commission-statement', period, userId, locationId],
    queryFn: () => getStatement(period, userId || undefined, locationId || undefined),
    retry: false,
  })

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Commission statement</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            What was sold in the month, what it earned, and what is payable.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {reps.length > 1 && (
            <label className="text-sm">
              <span className="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Rep
              </span>
              <select
                className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
                value={userId}
                onChange={(event) => setUserId(event.target.value)}
              >
                <option value="">Me</option>
                {reps
                  .filter((rep) => rep.id !== me?.id)
                  .map((rep) => (
                    <option key={rep.id} value={rep.id}>
                      {rep.name}
                    </option>
                  ))}
              </select>
            </label>
          )}
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Month
            </span>
            <Input
              type="month"
              className="mt-1 w-auto"
              value={period}
              onChange={(event) => setPeriod(event.target.value || currentPeriod())}
            />
          </label>
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Location
            </span>
            <select
              className={`mt-1 ${SELECT_CLASS}`}
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
            >
              <option value="">Any location</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
          <ExportButton
            filename={`commission-statement-${period}`}
            selectClassName={SELECT_CLASS}
            onExport={(format) =>
              exportAccruals(format, {
                period,
                user_id: userId || undefined,
                location_id: locationId || undefined,
              })
            }
            errorMessage="Could not export the statement."
          />
        </div>
      </header>

      <Card>
        <CardHeader
          title={`${data?.user_name ?? 'Statement'} — ${periodLabel(period)}`}
          description="Every figure here was frozen when the sale was booked. Editing a rate card later does not move it; a recompute does, and only when finance asks for one."
        />
        <QueryState
          isLoading={isLoading}
          error={error}
          isEmpty={data?.lines.length === 0}
          emptyMessage="No commission was earned in this month."
        >
          {data && (
            <>
              <div className="grid gap-4 border-b border-slate-200 p-5 dark:border-slate-800 tablet:grid-cols-4">
                <Figure
                  label="Sales"
                  value={data.basis_amount}
                  hint={`${data.invoice_count} document${data.invoice_count === 1 ? '' : 's'}, net of tax`}
                />
                <Figure label="Approved" value={data.approved_amount} hint="Payable" />
                <Figure
                  label="Pending"
                  value={data.pending_amount}
                  hint="Earned, awaiting collection"
                />
                <Figure label="Cancelled" value={data.cancelled_amount} hint="Invoice or sale voided" />
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <caption className="sr-only">Commission by document</caption>
                  <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                    <tr>
                      {/* Not "Invoice": a counter sale earns commission too, and it has a
                          receipt number rather than an invoice number. */}
                      <th className="px-5 py-2 font-medium">Document</th>
                      <th className="px-5 py-2 font-medium">Customer</th>
                      <th className="px-5 py-2 text-right font-medium">Sales</th>
                      <th className="px-5 py-2 text-right font-medium">Rate</th>
                      <th className="px-5 py-2 text-right font-medium">Commission</th>
                      <th className="px-5 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {data.lines.map((line) => (
                      <tr key={line.id}>
                        <td className="px-5 py-2 font-medium">
                          {mayOpenInvoice && line.invoice_number ? (
                            <Link to={`/invoices/${line.invoice_id}`} className="hover:underline">
                              {line.invoice_number}
                            </Link>
                          ) : (
                            // A receipt number is shown but never linked: a counter sale
                            // has no detail screen to open, and a dead link reads as a
                            // permission problem rather than as something that is not there.
                            (line.invoice_number ?? line.receipt_number ?? '—')
                          )}
                        </td>
                        <td className="px-5 py-2 text-slate-500 dark:text-slate-400">
                          {line.customer_name ?? '—'}
                        </td>
                        <td className="px-5 py-2 text-right tabular-nums">
                          {formatMoney(line.basis_amount)}
                        </td>
                        <td className="px-5 py-2 text-right tabular-nums text-slate-500 dark:text-slate-400">
                          {displayRate(line)}
                        </td>
                        <td className="px-5 py-2 text-right font-medium tabular-nums">
                          {formatMoney(line.amount)}
                          <Breakdown accrual={line} />
                        </td>
                        <td className="px-5 py-2">
                          <StatusBadge status={line.status} />
                          {line.cancel_reason && (
                            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                              {line.cancel_reason}
                            </p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </QueryState>
      </Card>
    </div>
  )
}
